// Defensive sanitizer for TipTap JSON content loaded into the Draft Canvas.
//
// Background: when a custom node type is removed from the editor schema but
// still exists in saved documents, TipTap throws `RangeError: Unknown node
// type: <name>` during editor initialization. That surfaces to users as the
// "reload page stutter" (the page mounts, the editor crashes, React tears
// down and re-mounts).
//
// This sanitizer walks incoming content and replaces any node whose type is
// NOT in the allowlist with an empty paragraph, so the editor always mounts
// even if we later retire another custom node without a back-compat shim.
// Standard block/inline nodes from StarterKit + our registered custom nodes
// pass through untouched.

const ALLOWED_NODE_TYPES = new Set<string>([
  // StarterKit + base extensions we configure.
  "doc",
  "paragraph",
  "text",
  "heading",
  "blockquote",
  "bulletList",
  "orderedList",
  "listItem",
  "horizontalRule",
  "hardBreak",
  // Custom Excerptus nodes currently registered in BriefEditor.
  "excerptus.signatureImage",
  "excerptus.footnoteRef",
  "excerptus.endnoteRef",
  "excerptus.bibliography",
  // Legacy compatibility shim (see LegacyImageCaptionNode).
  "excerptus.imageCaption",
]);

type AnyNode = { type?: string; content?: unknown[]; [k: string]: unknown };

export function sanitizeEditorContent(input: unknown): unknown {
  if (!input || typeof input !== "object") return input;
  const node = input as AnyNode;
  if (typeof node.type !== "string") return input;

  // Recurse into children first so we can still surface anything salvageable.
  const sanitizedChildren = Array.isArray(node.content)
    ? node.content
        .map((c) => sanitizeEditorContent(c))
        .filter((c) => c !== null)
    : undefined;

  if (!ALLOWED_NODE_TYPES.has(node.type)) {
    // Replace unknown node with an empty paragraph so the doc stays valid.
    return { type: "paragraph" };
  }

  return sanitizedChildren ? { ...node, content: sanitizedChildren } : node;
}
